/* eslint-disable max-len */
import React, { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Grid } from "@material-ui/core";
import styled from "styled-components";
import ReCaptcha from "react-google-recaptcha";
import FormController from "../../components/globals/FormController";
import PasswordHelper from "../../components/globals/passwordHelper";
import PasswordInput from "../../components/globals/PasswordInput";
import GlobalTheme from "../../styled/GlobalTheme";
import {
  CreateAccountButton,
  NameFormController,
} from "./CreateAccount.styled";
import useGetUserFormData from "./hooks/useGetUserFormData";
import useDidUpdateEffect from "../../hooks/useDidUpdateEffect";
import { SEVERITY } from "../../utils/constants/alerts";
import CaptchaProvider from "../../services/entities/CaptchaProvider";
import { ENV, GOOGLE_SITE_KEY, ENVIRONMENTS } from "../../utils/constants/globals";
import { AlertsContext } from "../../contexts/AlertsContext";
import { WebViewContext } from "../../contexts/WebViewContext";
import routes from "../../utils/constants/routes";
import usePlan from "../../components/PlanSelector/usePlan";

const Form = styled.form`
  width: 100%;
`;

const StyledCreateAccountButton = styled(CreateAccountButton)`
  && {
    width: 100%;
    max-width: 360px;
    margin-bottom: 20px;
    ${({ isApp, theme }) =>
      isApp &&
      `
      background: ${theme.colors.lightBlue};
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
      border-radius: 4px;
      max-width: 154px;
      margin: 14px;
    `}
    @media (max-width: 320px) {
      max-width: 270px;
    }
  }
`;

const ButtonContainer = styled(Grid)`
  && {
    text-align: center;
    padding-bottom: 30px;
    border-bottom: 1px solid #909090;
    ${({ isApp }) =>
      isApp &&
      `
      border-bottom: none;
      padding-bottom: 16px;
    `}
    @media (max-width: 320px) {
      text-align: inherit;
    }
  }
`;

const StyledNameFormController = styled(NameFormController)`
  && {
    margin: 0;
    opacity: ${({ opacity }) => opacity || "1"};
    margin-top: ${({ marginTop }) => marginTop || "0"};
    ${GlobalTheme.breakpoints.down("lg")} {
      width: 90%;
      ${({ isApp }) =>
        isApp &&
        `
        width: 100%;
      `}
    }

    ${GlobalTheme.breakpoints.down("sm")} {
      margin-top: 16px;
      width: 100%;
    }
  }
`;

const StyledFormController = styled(FormController)`
  && {
    margin: 0;
    opacity: ${({ opacity }) => opacity || "1"};
    ${({ isApp }) =>
      isApp &&
      `
      margin-top: 16px;
    `}
    @media (max-width: 320px) {
      width: 100%;
    }
    ${GlobalTheme.breakpoints.down("lg")} {
      width: 90%;
      ${({ isApp }) =>
        isApp &&
        `
        width: 100%;
      `}
    }

    ${GlobalTheme.breakpoints.down("sm")} {
      margin-top: 16px;
    }

    ${GlobalTheme.breakpoints.down("sm")} {
      margin-top: 16px;
      width: 100%;
    }
  }
`;

const StyledPasswordInput = styled(PasswordInput)`
  && {
    margin: 0;
    opacity: ${({ opacity }) => opacity || "1"};
    ${({ isApp }) =>
      isApp &&
      `
      margin-top: 16px;
    `}
    ${GlobalTheme.breakpoints.down("lg")} {
      width: 90%;
      ${({ isApp }) =>
        isApp &&
        `
        width: 100%;
      `}
    }
    @media (max-width: 767px) {
      width: 100%;
    }
    ${GlobalTheme.breakpoints.down("sm")} {
      margin-top: 16px;
    }
    @media (max-width: 320px) {
      width: 100%;
    }
  }
`;

const StyledPasswordHelper = styled(PasswordHelper)`
  opacity: ${({ opacity }) => opacity || "1"};
  position: absolute;
  top: 86px;
  left: 15px;
  width: 92%;
  ${GlobalTheme.breakpoints.down("md")} {
    position: unset;
    top: unset;
    left: unset;
    width: 100%;
  }
`;

const ContainerCaptcha = styled.div`
  margin-top: 25px;
  div > div {
    margin: auto;
  }
`;

const CreateAccountForm = ({
  wantsFree,
  className,
  planSelected,
  hideHolderBottom,
  hideMobileInput,
  collaboratorData,
}) => {
  const {
    user,
    setUser,
    setIsPasswordValid,
    handleChange,
    handleChangeNumbers,
    signUp,
    isEmpty,
  } = useGetUserFormData();

  const intl = useIntl();
  const { showAlert, showError } = useContext(AlertsContext);
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [currentToken, setCurrentToken] = useState("");
  const recaptchaRef = React.createRef();
  const { isApp, isAppIOS } = useContext(WebViewContext);
  const history = useHistory();

  const { plan } = usePlan()
  const enviromentForPlan = Object.keys(ENVIRONMENTS).find(key => ENVIRONMENTS[key] === ENV);

  useDidUpdateEffect(() => {
    setUser({
      ...user,
      wantsFree,
    });
  }, [wantsFree]);

  useEffect(() => {
    setUser({
      ...user,
      firstName: collaboratorData?.firstname || user.firstName,
      lastName: collaboratorData?.lastname || user.lastName,
      email: collaboratorData?.email || user.email,
      planSelected: {
        id: plan.id[enviromentForPlan],
        name: collaboratorData?.email ? 'collaborator' : plan.name
      }
    });
  }, [plan])

  const onChange = (token) => {
    if (!token) {
      setLoading(false);
      setButtonDisabled(true);
    }
    setCurrentToken(token);
    setButtonDisabled(false);
  };

  const createAccount = async () => {
    // history.push(routes.__TEST__);
    setLoading(true);
    const validate = await CaptchaProvider.validateCaptcha(currentToken);
    // validate.data.validateCaptcha.isValid
    if (true) {
      try {
        const response = await signUp();
        if (!response) {
          setButtonDisabled(false);
          setLoading(false);
        }
      } catch (e) {
        setButtonDisabled(false);
        setLoading(false);
        showError(intl.formatMessage({
          id: 'alert.error.loginEmail',
          defaultMessage: 'Your mail or password is incorrect. Please write again.',
        }));
      }
    } else {

    // recaptchaRef.current.reset();
    }
  };

  return (
    <Form className={className} planSelected={planSelected}>
      <Grid
        container
        direction="row"
        justify="space-between"
        spacing={
          window.innerWidth < GlobalTheme.breakpoints.values.lg ||
          isApp ||
          isAppIOS
            ? 0
            : 4
        }
      >
        <Grid
          item
          xs={12}
          md={isApp || isAppIOS ? 12 : 6}
          lg={isApp || isAppIOS ? 12 : 6}
        >
          <StyledNameFormController
            label={
              <FormattedMessage
                id="onboarding.creteAccount.firstname"
                description="Create account input first name"
                defaultMessage="First name"
              />
            }
            value={collaboratorData?.firstname || user.firstName}
            disabled={collaboratorData?.firstname ? true: false}
            placeholder="Liam"
            onChange={handleChange}
            id="firstName"
            hasHidingPlaceholder
            showRequired
            showRequiredError={isEmpty.firstName}
            opacity={planSelected ? "1" : "0.25"}
            isApp={isApp || isAppIOS}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={isApp || isAppIOS ? 12 : 6}
          lg={isApp || isAppIOS ? 12 : 6}
        >
          <StyledNameFormController
            label={
              <FormattedMessage
                id="onboarding.creteAccount.lastname"
                description="Create account input last name"
                defaultMessage="Last name"
              />
            }
            value={collaboratorData?.lastname || user.lastName}
            disabled={collaboratorData?.lastname ? true: false}
            placeholder="Smith"
            onChange={handleChange}
            id="lastName"
            hasHidingPlaceholder
            showRequired
            showRequiredError={isEmpty.lastName}
            opacity={planSelected ? "1" : "0.25"}
            isApp={isApp || isAppIOS}
            marginTop={isApp || isAppIOS ? "16px" : "0"}
          />
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justify="space-between"
        spacing={
          window.innerWidth < GlobalTheme.breakpoints.values.lg ||
          isApp ||
          isAppIOS
            ? 0
            : 4
        }
      >
        <Grid
          item
          xs={12}
          md={isApp || isAppIOS ? 12 : 6}
          lg={isApp || isAppIOS ? 12 : 6}
        >
          <StyledFormController
            label={
              <FormattedMessage
                id="onboarding.creteAccount.email"
                description="Create account input email"
                defaultMessage="Email"
              />
            }
            value={collaboratorData?.email || user.email}
            disabled={collaboratorData?.email ? true: false}
            placeholder="smith@mail.com"
            onChange={handleChange}
            id="email"
            hasHidingPlaceholder
            showRequired
            showRequiredError={isEmpty.email}
            opacity={planSelected ? "1" : "0.25"}
            holderBottom={!hideHolderBottom}
            messageHolder={intl.formatMessage({
              id: "onboarding.creteAccount.email.holder",
              defaultMessage:
                "You will receive an account verification link for this email.",
            })}
            isApp={isApp || isAppIOS}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={isApp || isAppIOS ? 12 : 6}
          lg={isApp || isAppIOS ? 12 : 6}
          style={{ position: "relative" }}
        >
          <StyledPasswordInput
            label={
              <FormattedMessage
                id="onboarding.creteAccount.password"
                description="Create account input password"
                defaultMessage="Password"
              />
            }
            value={user.password}
            type="password"
            onChange={handleChange}
            id="password"
            showRequired
            showRequiredError={isEmpty.password}
            opacity={planSelected ? "1" : "0.25"}
            isApp={isApp || isAppIOS}
          />
          {user.password && (
            <StyledPasswordHelper
              password={user.password}
              setIsValid={setIsPasswordValid}
              opacity={planSelected ? "1" : "0.25"}
            />
          )}
        </Grid>
      </Grid>
      {!hideMobileInput && (
        <Grid
          container
          direction={
            window.innerWidth < GlobalTheme.breakpoints.values.lg &&
            window.innerWidth > GlobalTheme.breakpoints.values.sm
              ? "row"
              : "column"
          }
          justify="space-between"
          spacing={
            window.innerWidth < GlobalTheme.breakpoints.values.lg ? 0 : 4
          }
        >
          <Grid item xs={12} md={6} lg={6}>
            <StyledFormController
              label={
                <FormattedMessage
                  id="onboarding.creteAccount.mobile"
                  description="Create account input mobile"
                  defaultMessage="Mobile"
                />
              }
              value={user.mobile}
              placeholder="5512345678"
              onChange={handleChangeNumbers}
              id="mobile"
              hasHidingPlaceholder
              showRequiredError={isEmpty.mobile}
              opacity={planSelected ? "1" : "0.25"}
              type="tel"
            />
          </Grid>
        </Grid>
      )}
      <ContainerCaptcha>
        <ReCaptcha
          ref={recaptchaRef}
          sitekey={GOOGLE_SITE_KEY}
          size="normal"
          hl={intl.locale}
          onChange={onChange}
        />
      </ContainerCaptcha>
      <ButtonContainer item isApp={isApp || isAppIOS}>
        <StyledCreateAccountButton
          handlerClick={createAccount}
          // disabled={!planSelected || buttonDisabled}
          disabled={false}
          isLoading={loading}
          isApp={isApp || isAppIOS}
        >
          <FormattedMessage
            id="onboarding.creteAccount.signUp"
            defaultMessage="Sign up!"
          />
        </StyledCreateAccountButton>
      </ButtonContainer>
    </Form>
  );
};

CreateAccountForm.propTypes = {
  wantsFree: PropTypes.bool.isRequired,
  className: PropTypes.string,
  planSelected: PropTypes.string,
  hideHolderBottom: PropTypes.bool,
  hideMobileInput: PropTypes.bool,
};

CreateAccountForm.defaultProps = {
  className: "",
  planSelected: "",
  hideHolderBottom: false,
  hideMobileInput: false,
};

export default CreateAccountForm;
